import useResizeObserver from '@utils/hooks/useResizeObserver';
import useVerticalGrabScroll from '@utils/hooks/useVerticalGrabScroll';
import useVerticalScrollShadows from '@utils/hooks/useVerticalScrollShadows';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import {
  PropsWithChildren,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';

export interface IShadowContainerVerticalRef {
  readonly scrollTo: (offset: number, behavior?: ScrollBehavior) => void;
}

interface Props {
  readonly className?: string;
  readonly hasDefaultHeightClass?: boolean;
  readonly customScrollbarTheme?: 'light' | 'dark';
  readonly hideScrollbar?: boolean;
  readonly shadowColour?: string;
  readonly disableScroll?: boolean;
  readonly shadowHeight?: string;
}

const ShadowContainerVertical = forwardRef<
  IShadowContainerVerticalRef,
  PropsWithChildren<Props>
>(
  (
    {
      children,
      hasDefaultHeightClass = true,
      className,
      customScrollbarTheme,
      disableScroll = false,
      shadowColour = 'from-white dark:from-black-100',
      hideScrollbar = true,
      shadowHeight = 'h-10',
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerRefCallback, { height: containerHeight }] =
      useResizeObserver();
    const [contentRefCallback, { height: contentHeight }] = useResizeObserver();
    const { isStart, isEnd } = useVerticalScrollShadows(
      containerRef,
      containerHeight,
      contentHeight,
      disableScroll
    );
    useVerticalGrabScroll(
      containerRef,
      containerHeight,
      contentHeight,
      disableScroll
    );

    useImperativeHandle(ref, () => ({
      scrollTo: (topOffset: number, behavior: ScrollBehavior = 'smooth') => {
        containerRef.current?.scrollTo({ top: topOffset, behavior });
      },
    }));

    return (
      <div
        className={classNames(className, 'relative', {
          'h-full': hasDefaultHeightClass,
        })}
      >
        <AnimatePresence>
          {!isStart && !disableScroll && (
            <motion.span
              animate={{ opacity: 1 }}
              className={classNames(
                shadowColour,
                shadowHeight,
                `pointer-events-none absolute top-0 z-10 w-full bg-gradient-to-b to-transparent`
              )}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            />
          )}
        </AnimatePresence>

        <div
          ref={(node) => {
            containerRef.current = node;
            containerRefCallback(node);
          }}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: non-interactive tabindex allowed for now
          tabIndex={0}
          className={classNames(
            {
              'scrollbar-hide': hideScrollbar,
              'overflow-y-hidden': disableScroll,
              'overflow-y-auto': !disableScroll,
              'custom-scrollbar': !disableScroll && customScrollbarTheme,
              'custom-scrollbar--light': customScrollbarTheme === 'light',
              'custom-scrollbar--dark': customScrollbarTheme === 'dark',
            },
            'relative h-full select-none'
          )}
        >
          <div ref={contentRefCallback}>{children}</div>
        </div>

        <AnimatePresence>
          {!isEnd && !disableScroll && (
            <motion.span
              animate={{ opacity: 1 }}
              className={classNames(
                shadowColour || 'from-white dark:from-black-100',
                shadowHeight,
                `pointer-events-none absolute bottom-0 z-10 w-full bg-gradient-to-t to-transparent`
              )}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }
);

export default ShadowContainerVertical;
